import { db } from '../config/firebase';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    arrayUnion,
    query,
    orderBy,
    where
} from 'firebase/firestore';
import { User } from '../types/users';
import { orderService } from './orderService';
import { favoriteService } from './favoriteService';
import { cartService } from './cartService';
import { reviewService } from './reviewService';

const COLLECTION_NAME = 'users';

export const userService = {
    async addUser(user: User) {
        try {
            const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                ...user,
                createdAt: new Date(),
                updatedAt: new Date()
            });
            return { id: docRef.id, ...user };
        } catch (error) {
            throw new Error('Không thể thêm người dùng: ' + error);
        }
    },

    async getAllUsers() {
        try {
            const usersRef = collection(db, COLLECTION_NAME);
            const q = query(usersRef, orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as User[];
        } catch (error) {
            throw new Error('Không thể lấy danh sách người dùng: ' + error);
        }
    },

    async updateUser(id: string, user: Partial<User>) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            await updateDoc(docRef, {
                ...user,
                updatedAt: new Date()
            });
            return { id, ...user };
        } catch (error) {
            throw new Error('Không thể cập nhật người dùng: ' + error);
        }
    },

    async updateUserByLocalId(localId: string, user: Partial<User>) {
        try {
            const usersRef = collection(db, COLLECTION_NAME);
            const q = query(usersRef, where('localId', '==', localId));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                throw new Error('Không tìm thấy người dùng với localId này');
            }

            const userDoc = querySnapshot.docs[0];
            await updateDoc(userDoc.ref, {
                ...user,
                updatedAt: new Date()
            });

            return { id: userDoc.id, ...user };
        } catch (error) {
            throw new Error('Không thể cập nhật người dùng: ' + error);
        }
    },

    async deleteUser(id: string) {
        try {
            // 1. Delete all user's orders
            const userOrders = await orderService.getAllOrders();
            const filteredOrders = userOrders.filter(order => order.userId === id);
            await Promise.all(filteredOrders.map((order: any) => orderService.deleteOrder(order.id)));

            // 2. Delete all user's favorites
            const favorites = await favoriteService.getUserFavorites(id);
            await Promise.all(favorites.map((fav: any) => favoriteService.deleteFavorite(fav.id)));

            // 3. Clear user's cart
            await cartService.clearCart(id);

            // 4. Delete all user's reviews

            const review = await reviewService.getUserReviews(id);
            await Promise.all(review.map(review => reviewService.deleteReview(review.id)));

            // 5. Finally delete the user document
            await deleteDoc(doc(db, COLLECTION_NAME, id));

            return true;
        } catch (error) {
            throw new Error('Không thể xóa người dùng và dữ liệu liên quan: ' + error);
        }
    },

    async getUserById(id: string) {
        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);
        return docSnap.data() as User;
    },

    async updateUserPoints(userId: string, orderId: string, points: number, date: Date) {
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                points,
                lstPoint: arrayUnion({ orderId, point: points, date: date })
            });
            return true;
        } catch (error) {
            throw new Error('Could not update user points: ' + error);
        }
    },

    getUserTags: async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'users'));
            const tags = new Set<string>();
            querySnapshot.docs.forEach(doc => {
                const userData = doc.data();
                if (userData.tagNames) {
                    tags.add(userData.tagNames);
                }
            });
            return Array.from(tags);
        } catch (error) {
            throw new Error('Could not fetch user tags: ' + error);
        }
    },
}; 